<script>
  import AdminNav from '@/components/admin/AdminNav.svelte';
  import { router } from '@inertiajs/svelte'

  let { comments = [], pagination = {}, boards = [], filters = {}, stats = {}, deleted_count = 0, expiring_soon_count = 0, is_super_admin = false, flash = {} } = $props()

  // svelte-ignore state_referenced_locally
  const initialSearchQuery = filters?.q || ''
  // svelte-ignore state_referenced_locally
  const initialStatusFilter = filters?.status || ''
  // svelte-ignore state_referenced_locally
  const initialBoardFilter = filters?.board_id || ''
  let searchQuery = $state(initialSearchQuery)
  let statusFilter = $state(initialStatusFilter)
  let boardFilter = $state(initialBoardFilter)
  let selectedIds = $state([])
  let selectAll = $state(false)

  let isDeletedView = $derived(statusFilter === 'deleted')

  function applyFilters() {
    const params = {}
    if (searchQuery) params.q = searchQuery
    if (statusFilter) params.status = statusFilter
    if (boardFilter) params.board_id = boardFilter
    selectedIds = []
    selectAll = false
    router.get('/admin/comments', params, { preserveState: true })
  }

  function goToPage(page) {
    router.get('/admin/comments', { ...filters, page }, { preserveState: true })
  }

  function getStatusBadge(status) {
    switch (status) {
      case 'approved': return 'badge-success'
      case 'pending': return 'badge-info'
      case 'flagged': return 'badge-warning'
      case 'rejected': return 'badge-error'
      default: return 'badge-neutral'
    }
  }

  function toggleSelectAll() {
    selectAll = !selectAll
    selectedIds = selectAll ? comments.map(c => c.id) : []
  }

  function toggleSelect(id) {
    if (selectedIds.includes(id)) {
      selectedIds = selectedIds.filter(i => i !== id)
    } else {
      selectedIds = [...selectedIds, id]
    }
  }

  function batchAction(action) {
    if (selectedIds.length === 0) return
    const label = action === 'approve' ? '승인' : action === 'reject' ? '반려' : '삭제'
    if (confirm(`선택한 ${selectedIds.length}개 댓글을 ${label}하시겠습니까?`)) {
      router.post('/admin/comments/batch_action', { batch_action: action, ids: selectedIds })
    }
  }

  function restoreComment(id) {
    router.post(`/admin/comments/${id}/restore`)
  }

  function permanentDeleteComment(id) {
    if (confirm('이 댓글을 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      router.delete(`/admin/comments/${id}/permanent_delete`)
    }
  }

  function getDaysUntilExpiry(discardedAt) {
    if (!discardedAt) return null
    const discardDate = new Date(discardedAt)
    const expiryDate = new Date(discardDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    const now = new Date()
    const days = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
    return Math.max(0, days)
  }
</script>

<AdminNav activeTab="comments" />

  <main>
    {#if flash?.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    {#if flash?.alert}
      <div class="mb-6 p-4 bg-error-light dark:bg-error/20 border border-error/30 rounded-xl text-error-dark dark:text-error text-body2">
        {flash.alert}
      </div>
    {/if}

    <!-- Stats -->
    <div class="flex items-center gap-6 mb-6">
      <h2 class="text-h1 text-content">댓글 관리</h2>
      <div class="flex items-center gap-4 text-body2">
        <span class="px-3 py-1 bg-surface-tertiary text-content-secondary rounded-lg">
          전체 {stats.total || 0}개
        </span>
        {#if stats.flagged > 0}
          <span class="px-3 py-1 bg-warning-light dark:bg-warning/20 text-warning-dark dark:text-warning rounded-lg font-medium">
            플래그 {stats.flagged}개
          </span>
        {/if}
        {#if stats.blinded > 0}
          <span class="px-3 py-1 bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 rounded-lg font-medium">
            블라인드 {stats.blinded}개
          </span>
        {/if}
      </div>
    </div>

    <!-- Filters -->
    <div class="card card-bordered p-4 mb-6 shadow-md">
      <div class="flex flex-wrap items-center gap-4">
        {#if !isDeletedView}
          <input
            type="text"
            bind:value={searchQuery}
            placeholder="댓글 내용 검색..."
            class="input flex-1 min-w-[200px] py-2.5"
          />
        {/if}

        <select bind:value={statusFilter} class="select">
          <option value="">모든 상태</option>
          <option value="approved">승인됨</option>
          <option value="pending">대기중</option>
          <option value="flagged">플래그됨</option>
          <option value="rejected">반려됨</option>
          <option value="deleted">삭제됨 ({deleted_count})</option>
        </select>

        {#if !isDeletedView}
          <select bind:value={boardFilter} class="select">
            <option value="">모든 게시판</option>
            {#each boards as board}
              <option value={board.id}>{board.name}</option>
            {/each}
          </select>
        {/if}

        <button onclick={applyFilters} class="btn btn-primary btn-md">검색</button>
      </div>
    </div>

    <!-- Deleted Warning Banner -->
    {#if isDeletedView}
      <div class="mb-6 p-4 bg-warning-light dark:bg-warning/20 border border-warning/30 rounded-xl text-warning-dark dark:text-warning text-body2">
        삭제된 항목은 30일 후 자동 영구삭제됩니다.
        {#if expiring_soon_count > 0}
          <span class="ml-2 font-semibold">(23일+ 경과: {expiring_soon_count}개)</span>
        {/if}
      </div>
    {/if}

    <!-- Batch Action Bar -->
    {#if selectedIds.length > 0 && !isDeletedView}
      <div class="mb-4 p-3 bg-primary-50 dark:bg-primary/10 rounded-xl flex items-center gap-3">
        <span class="text-body2 text-primary font-medium">{selectedIds.length}개 선택됨</span>
        <button onclick={() => batchAction('approve')} class="btn btn-success btn-sm">일괄 승인</button>
        <button onclick={() => batchAction('reject')} class="btn btn-secondary btn-sm">일괄 반려</button>
        <button onclick={() => batchAction('delete')} class="btn btn-error btn-sm">일괄 삭제</button>
      </div>
    {/if}

    <!-- Comments Table -->
    <div class="card card-bordered overflow-hidden shadow-md">
      <table class="w-full">
        <thead class="bg-surface-tertiary">
          <tr>
            {#if !isDeletedView}
              <th class="px-4 py-3 w-10">
                <input type="checkbox" checked={selectAll} onchange={toggleSelectAll} class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" />
              </th>
            {/if}
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">내용</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">게시글</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">작성자</th>
            {#if isDeletedView}
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">삭제일</th>
              <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">만료</th>
            {:else}
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">상태</th>
              <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">추천</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">작성일</th>
            {/if}
            <th class="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each comments as comment}
            <tr class="hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors {isDeletedView ? 'opacity-75' : ''}">
              {#if !isDeletedView}
                <td class="px-4 py-4">
                  <input type="checkbox" checked={selectedIds.includes(comment.id)} onchange={() => toggleSelect(comment.id)} class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" />
                </td>
              {/if}
              <td class="px-6 py-4">
                <div class="text-body2 text-content truncate max-w-[300px]">
                  {#if comment.is_blinded}
                    <span class="inline-block mr-1 px-1.5 py-0.5 text-caption bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 rounded font-medium">블라인드</span>
                  {/if}
                  {comment.content?.substring(0, 100)}{comment.content?.length > 100 ? '...' : ''}
                </div>
              </td>
              <td class="px-6 py-4">
                <a href={`/admin/posts/${comment.post_id}`} class="text-body2 text-primary hover:text-primary-dark truncate max-w-[150px] block">
                  {comment.post?.title || '-'}
                </a>
              </td>
              <td class="px-6 py-4 text-body2 text-content-secondary">
                {comment.user?.name || '익명'}
              </td>
              {#if isDeletedView}
                <td class="px-6 py-4 text-body2 text-content-tertiary">
                  {comment.discarded_at ? new Date(comment.discarded_at).toLocaleDateString('ko-KR') : '-'}
                </td>
                <td class="px-6 py-4 text-center">
                  {#if comment.discarded_at}
                    {@const days = getDaysUntilExpiry(comment.discarded_at)}
                    <span class="text-caption font-medium {days <= 7 ? 'text-error' : 'text-warning-dark dark:text-warning'}">
                      {days}일 남음
                    </span>
                  {/if}
                </td>
              {:else}
                <td class="px-6 py-4">
                  <span class={`badge ${getStatusBadge(comment.moderation_status)}`}>
                    {comment.moderation_status || 'approved'}
                  </span>
                </td>
                <td class="px-6 py-4 text-center text-body2 text-content-tertiary">
                  {comment.likes_count || 0}
                </td>
                <td class="px-6 py-4 text-body2 text-content-tertiary">
                  {new Date(comment.created_at).toLocaleDateString('ko-KR')}
                </td>
              {/if}
              <td class="px-6 py-4 text-right">
                {#if isDeletedView}
                  <div class="flex items-center justify-end gap-2">
                    <button onclick={() => restoreComment(comment.id)} class="text-success hover:text-success-dark text-body2 font-medium">복구</button>
                    {#if is_super_admin}
                      <button onclick={() => permanentDeleteComment(comment.id)} class="text-error hover:text-error-dark text-body2 font-medium">영구삭제</button>
                    {/if}
                  </div>
                {:else}
                  <div class="flex items-center justify-end gap-2">
                    <a href={`/admin/comments/${comment.id}`} class="text-primary hover:text-primary-dark text-body2 font-medium">상세</a>
                    {#if comment.moderation_status === 'flagged'}
                      <button
                        onclick={() => router.post(`/admin/comments/${comment.id}/approve`)}
                        class="text-success text-body2 font-medium hover:text-success-dark"
                      >승인</button>
                      <button
                        onclick={() => { if (confirm('이 댓글을 삭제하시겠습니까?')) router.post(`/admin/comments/${comment.id}/reject`) }}
                        class="text-error text-body2 font-medium hover:text-error-dark"
                      >삭제</button>
                    {/if}
                  </div>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if comments.length === 0}
        <div class="text-center py-12 text-content-tertiary">
          {isDeletedView ? '삭제된 댓글이 없습니다' : '검색 결과가 없습니다'}
        </div>
      {/if}
    </div>

    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-6">
        <button onclick={() => goToPage(pagination.current_page - 1)} disabled={pagination.current_page === 1} class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed">이전</button>
        <span class="px-4 py-2 text-body2 text-content-tertiary">{pagination.current_page} / {pagination.total_pages}</span>
        <button onclick={() => goToPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.total_pages} class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed">다음</button>
      </div>
    {/if}
  </main>
